import { LogoMark } from '@/components/ui/logo';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { type NavState, useNavState } from '@/hooks/use-nav-state';
import { cn } from '@/lib/cn';
import { useProfile } from '@/stores/profile';
import { useUiPref } from '@/stores/ui';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  History,
  MessageSquare,
  Sliders,
  Sparkles,
  Sun,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { Link, useLocation } from 'react-router';

interface NavItem {
  href: string;
  label: string;
  Icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  /** Key into useNavState() for live subtitle; omit for static hint. */
  stateKey?: keyof NavState;
  hint?: string;
}

const NAV_TODAY: NavItem[] = [
  { href: '/spark', label: 'Spark', Icon: Sparkles, stateKey: 'spark' },
  { href: '/today', label: 'Today', Icon: Sun, stateKey: 'today' },
];

const NAV_LEARN: NavItem[] = [
  { href: '/notes', label: 'Study', Icon: BookOpen, stateKey: 'notes' },
  { href: '/pyq', label: 'Practice', Icon: History, hint: 'PYQ and tests' },
  { href: '/tutor', label: 'Tutor', Icon: MessageSquare, stateKey: 'tutor' },
];

export function Sidebar() {
  const { pathname } = useLocation();
  const collapsed = useUiPref((s) => s.sidebarCollapsed);
  const toggle = useUiPref((s) => s.toggleSidebar);
  const navState = useNavState();

  return (
    <aside
      className={cn(
        'sticky top-0 hidden h-[100dvh] shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)] transition-[width] duration-[var(--duration)] ease-[var(--ease-out)] md:flex',
        collapsed ? 'w-20' : 'w-64',
      )}
    >
      <div
        className={cn(
          'flex border-b border-[var(--color-border)] py-5',
          collapsed ? 'flex-col items-center gap-2 px-2' : 'items-center gap-2 px-5',
        )}
      >
        <LogoMark className="size-7 shrink-0 text-[var(--color-primary)]" />
        {!collapsed && (
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-sm font-semibold tracking-tight">Disha</span>
            <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
              EAPCET Math
            </span>
          </div>
        )}
        <ThemeToggle compact />
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="grid size-8 place-items-center rounded-md text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
        >
          {collapsed ? (
            <ChevronRight className="size-4" strokeWidth={1.75} />
          ) : (
            <ChevronLeft className="size-4" strokeWidth={1.75} />
          )}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 text-sm">
        <NavSection
          items={NAV_TODAY}
          pathname={pathname}
          collapsed={collapsed}
          navState={navState}
        />
        <NavSection
          title="Learn"
          items={NAV_LEARN}
          pathname={pathname}
          collapsed={collapsed}
          navState={navState}
        />
      </nav>

      <div className="shrink-0 border-t border-[var(--color-border)]">
        <Link
          to="/settings"
          title="Settings"
          className={cn(
            'flex items-center text-xs font-medium text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] transition-colors',
            collapsed ? 'justify-center px-2 py-3' : 'gap-2 px-4 py-3',
            pathname === '/settings' && 'text-[var(--color-foreground)]',
          )}
        >
          <Sliders className="size-4" strokeWidth={1.75} />
          {!collapsed && 'Settings'}
        </Link>
        <UserFooter collapsed={collapsed} />
      </div>
    </aside>
  );
}

function UserFooter({ collapsed }: { collapsed: boolean }) {
  const name = useProfile((s) => s.name);
  const id = useProfile((s) => s.studentId);

  const initials = (name || 'demo')
    .split(/\s+/)
    .map((value) => value[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div
      className={cn('flex items-center py-3', collapsed ? 'justify-center px-2' : 'gap-2 px-4')}
      title={collapsed ? name || 'Demo learner' : undefined}
    >
      <div className="grid size-8 place-items-center rounded-full bg-[var(--color-primary-soft)] text-[11px] font-semibold text-[var(--color-primary-soft-foreground)]">
        {initials || 'DL'}
      </div>
      {!collapsed && (
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-xs font-medium">{name || 'Demo learner'}</div>
          <div
            className="truncate font-mono text-[10px] text-[var(--color-muted-foreground)]"
            title={id}
          >
            {id ? id.slice(0, 10) : '—'}
          </div>
        </div>
      )}
    </div>
  );
}

function NavSection({
  title,
  items,
  pathname,
  collapsed,
  navState,
}: {
  title?: string;
  items: NavItem[];
  pathname: string;
  collapsed: boolean;
  navState: NavState;
}) {
  return (
    <div className="mb-5">
      {!collapsed && title && (
        <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-subtle-foreground)]">
          {title}
        </div>
      )}
      <ul className="space-y-0.5">
        {items.map((item) => {
          const active = pathname === item.href || pathname.startsWith(`${item.href}/`);
          const tab = item.stateKey ? navState[item.stateKey] : null;
          const subtitle = tab?.subtitle ?? item.hint;
          const emphasized = tab?.emphasized ?? false;
          return (
            <li key={item.href}>
              <Link
                to={item.href}
                title={item.label}
                className={cn(
                  'group flex items-center rounded-lg py-2 transition-colors duration-[var(--duration-fast)]',
                  collapsed ? 'justify-center px-2' : 'gap-3 px-2.5',
                  active
                    ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary-soft-foreground)]'
                    : 'text-[var(--color-foreground)] hover:bg-[var(--color-muted)]',
                )}
              >
                <item.Icon
                  className={cn(
                    'size-4 shrink-0',
                    active ? 'text-[var(--color-primary)]' : 'text-[var(--color-muted-foreground)]',
                  )}
                  strokeWidth={1.75}
                />
                {!collapsed && (
                  <span className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="truncate text-[13px] font-medium">{item.label}</span>
                    {subtitle && (
                      <span
                        className={cn(
                          'truncate text-[10px]',
                          emphasized && !active
                            ? 'text-[var(--color-primary)]'
                            : 'text-[var(--color-subtle-foreground)]',
                        )}
                      >
                        {subtitle}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
